/**
 * SCR-03 shop interior (MT-02): 10 upgrade slots × 3 styles, coins + star gate, 1-second build
 * animation with confetti, Grand Opening after the 10th. Flow A: the first upgrade is free,
 * the second one leads to "Name your shop" and the soft save-progress prompt. Decorate mode
 * (shop decor): the decor spots glow, a tap opens the sheet of owned pieces that fit, and a
 * placement is predicted locally and queued as a `decor` sync op like an upgrade.
 */
import * as Phaser from 'phaser';
import { cityShops, shopDef, type ShopDef } from '../config/content.js';
import { app } from '../game/context.js';
import { safeInsets } from '../game/layout.js';
import { decorSpotLookup } from '../game/decorLayout.js';
import { shopLayoutLookup, type Placement, type ShopLookup } from '../game/shopLayout.js';
import { vp } from '../game/viewport.js';
import { deco, dur, wait } from '../fx/motion.js';
import { Fx } from '../fx/particles.js';
import { t } from '../i18n/index.js';
import { openView } from '../features/registry.js';
import { MARK, mark } from '../services/firstSession.js';
import { op } from '../services/ops.js';
import { applyDecor, DECOR, decorDiff, placedIn, placementOf, spotDef, type DecorRefusal, type DecorSheetResult } from '../services/decor.js';
import { currentLevelNumber, nextUpgrade, upgradeCount } from '../services/progress.js';
import { applyUpgrade, isGrandOpeningDue } from '../services/rewards.js';
import { noteUpgrade } from '../services/review.js';
import type { Style } from '../services/state.js';
import { shopTitle } from '../ui/hud.js';
import { fadeIn, go, onResizeSettled } from './nav.js';

export interface ShopData {
  shopId: string;
  /** SO-06 read-only visit of a friend's shop, with the decor they placed (spot → decor id) */
  visit?: { owner: string; upgrades: Record<string, Style>; decor?: Record<string, string>; trophy: boolean; back: () => void };
}

/** once per session: the owned decor that is not on a wall yet (`a.session.checked`) */
const DECOR_HINT = 'shop:decor';

export class ShopScene extends Phaser.Scene {
  private shop!: ShopDef;
  private base!: Phaser.GameObjects.Image;
  private pieces = new Map<string, Phaser.GameObjects.Image>();
  private ghost: Phaser.GameObjects.Image | null = null;
  private selected: Record<string, Style> = {};
  private building = false;
  private lookup!: ShopLookup;
  private fx!: Fx;
  private visit: ShopData['visit'] | null = null;
  /** decor spots in the base image (normalised like slots); empty without the art module */
  private decorSpots: Record<string, Placement> = {};
  private decorImgs = new Map<string, Phaser.GameObjects.Image>();
  /** decor on screen: spot → decor id */
  private decorShown: Record<string, string> = {};
  private decorating = false;
  private decorMarks: Phaser.GameObjects.GameObject[] = [];
  private decorBusy = false;
  /** the decor sheet was asked for once (a snapshot may bring decor before it is loaded) */
  private decorArtAsked = false;

  /** built and showing (test hooks) */
  ready = false;

  constructor() {
    super('shop');
  }

  init(data: ShopData): void {
    this.ready = false;
    this.shop = shopDef(data.shopId) ?? cityShops(app().city)[0]!;
    this.pieces = new Map();
    this.ghost = null;
    this.selected = {};
    this.building = false;
    this.visit = data.visit ?? null;
    this.decorSpots = {};
    this.decorImgs = new Map();
    this.decorShown = {};
    this.decorating = false;
    this.decorMarks = [];
    this.decorBusy = false;
    this.decorArtAsked = false;
  }

  create(): void {
    const a = app();
    a.services.audio.music('shop');
    a.ui.setBusy(true);
    // the decor sheet only when this shop shows decor; Decorate mode loads it on demand
    this.decorArtAsked = Object.keys(this.decorNow()).length > 0;
    void Promise.all([a.art.loadRest(a.city), this.prepareLookup(), a.art.ensureGroup('rare'), this.decorArtAsked ? a.art.ensureGroup('decor') : null]).then(() => {
      a.ui.refresh(); // rare-item sprites in the sheet
      if (!this.scene.isActive()) return;
      a.ui.setBusy(false);
      this.build();
      fadeIn(this);
    });
    onResizeSettled(this, () => {
      if (!this.building && !a.ui.top) this.scene.restart({ shopId: this.shop.id, ...(this.visit ? { visit: this.visit } : {}) });
    });
    // placements that did not come from a tap here (a snapshot that undid a prediction) redraw too
    const off = a.services.store.subscribe(() => {
      if (this.ready) this.showDecor(true);
    });
    this.events.once('shutdown', off);
  }

  private async prepareLookup(): Promise<void> {
    const [lookup, spots] = await Promise.all([shopLayoutLookup(), decorSpotLookup()]);
    this.lookup = lookup;
    // only the spots the decor catalog knows (content/catalog/decor.json)
    this.decorSpots = Object.fromEntries(Object.entries(spots).filter(([id]) => spotDef(id)));
  }

  private baseKey(): string {
    return this.shop.id.replace(/^([a-z]+)_/, 'shp_$1_') + '_base';
  }

  private pieceKey(slot: string, style: string): string {
    return this.shop.id.replace(/^([a-z]+)_/, 'shp_$1_') + `_${slot}_${style}`;
  }

  private build(): void {
    const a = app();
    const { width: W, height: H } = this.scale;
    const u = vp.u;
    const safe = safeInsets();
    const bg = this.add.graphics();
    bg.fillGradientStyle(0xfff0d6, 0xfff0d6, 0xffd9a8, 0xffd9a8, 1);
    bg.fillRect(0, 0, W, H);
    // phones: interior above the upgrade sheet; wide screens: interior left, sheet right
    const wide = vp.cssW / vp.cssH > 1.25 && vp.cssW >= 900;
    const top = (safe.top + 66) * u;
    const area = wide
      ? { x: 16 * u, y: top, w: W - 452 * u, h: H - top - 20 * u }
      : { x: 0, y: top, w: W, h: H * 0.53 - top };
    const f = a.art.get(this.baseKey());
    this.base = this.add.image(area.x + area.w / 2, area.y + area.h / 2, f.texture, f.frame).setDepth(1);
    const k = Math.min((area.w * 0.97) / this.base.width, area.h / this.base.height);
    this.base.setScale(k);
    const frameG = this.add.graphics().setDepth(0);
    frameG.fillStyle(0x4a2c2a, 1);
    frameG.fillRoundedRect(this.base.x - this.base.displayWidth / 2 - 5 * u, this.base.y - this.base.displayHeight / 2 - 5 * u, this.base.displayWidth + 10 * u, this.base.displayHeight + 10 * u, 18 * u);
    const st = a.services.store.state;
    const owned = this.visit ? this.visit.upgrades : (st.shops[this.shop.id]?.upgrades ?? {});
    for (const up of this.shop.upgrades) {
      const style = owned[up.slot];
      if (style) this.placePiece(up.slot, up.id, style, false);
    }
    this.fx = new Fx(this);
    this.showDecor(false);
    const next = this.visit ? null : nextUpgrade(st, this.shop.id);
    if (next) this.showGhost(next.slot, next.id, 'a');
    this.setHud();
    this.ready = true;
    this.hintDecor();
  }

  /**
   * Owned decor (`meta.decor`) that fills none of the spots in any shop. Album prizes and the Rush
   * top-3's arrive while the player is reading an inbox message or finishing a sticker set, never
   * while they stand in the shop, so an earned piece can sit in the inventory unseen: nothing in
   * the interior changes until it is placed. Only the pieces that fit a spot this interior offers
   * count — the art module decides which spots exist, so a build without it has none.
   */
  private unplacedDecor(): number {
    const st = app().services.store.state;
    const types = new Set(Object.keys(this.decorSpots).map((id) => spotDef(id)!.type));
    return DECOR.filter((d) => types.has(d.spot) && (st.decor[d.id] ?? 0) > 0 && !placementOf(st, d.id)).length;
  }

  /** Point at the Decorate button once a session while a prize is still in the box. */
  private hintDecor(): void {
    const a = app();
    if (this.visit || a.session.checked.has(DECOR_HINT)) return;
    const n = this.unplacedDecor();
    if (!n) return;
    a.session.checked.add(DECOR_HINT);
    a.ui.toast(t('shop.decorWaiting', { n }));
  }

  /** An atlas frame centred in a placement rectangle of the base, sized to it (aspect kept). */
  private fitImage(p: Placement, key: string): Phaser.GameObjects.Image {
    const f = app().art.get(key);
    const b = this.base;
    const x = b.x - b.displayWidth / 2 + (p.x + p.w / 2) * b.displayWidth;
    const y = b.y - b.displayHeight / 2 + (p.y + p.h / 2) * b.displayHeight;
    const img = this.add.image(x, y, f.texture, f.frame).setDepth(10 + p.z);
    img.setScale(Math.min((p.w * b.displayWidth) / img.width, (p.h * b.displayHeight) / img.height));
    return img;
  }

  /** Piece image centred in its slot rectangle, sized to the rectangle (aspect kept). */
  private pieceImage(slot: string, style: string): Phaser.GameObjects.Image {
    return this.fitImage(this.lookup(slot), this.pieceKey(slot, style));
  }

  /** The decor this shop shows: the friend's on a visit, else the player's (with local predictions). */
  private decorNow(): Record<string, string> {
    return this.visit ? { ...(this.visit.decor ?? {}) } : placedIn(app().services.store.state, this.shop.id);
  }

  /** Bring the decor on screen in line with decorNow(): pieces drop in and pop out when `animate`. */
  private showDecor(animate: boolean): void {
    const a = app();
    const want = this.decorNow();
    const changed = decorDiff(this.decorShown, want);
    // decor from another device's placement: load its sheet once, then draw (fallback art without an atlas)
    if (!this.decorArtAsked && Object.values(changed).some((id) => id) && !a.art.groupLoaded('decor')) {
      this.decorArtAsked = true;
      void a.art.ensureGroup('decor').then(() => this.scene.isActive() && this.showDecor(animate));
      return;
    }
    for (const [spot, id] of Object.entries(changed)) {
      const old = this.decorImgs.get(spot);
      if (old) {
        this.decorImgs.delete(spot);
        if (animate) this.decorOut(old);
        else old.destroy();
      }
      const p = this.decorSpots[spot];
      if (!id || !p) continue;
      const img = this.fitImage(p, id);
      this.decorImgs.set(spot, img);
      if (animate) this.decorIn(img);
    }
    this.decorShown = want;
    if (this.decorating && Object.keys(changed).length) this.drawMarks();
  }

  /** Placed: a short drop with a bounce and a sparkle (the build animation, lighter). */
  private decorIn(img: Phaser.GameObjects.Image): void {
    const d = dur(520);
    if (!d) return;
    const s = img.scale;
    const y = img.y;
    img.setScale(s * 0.3).setAlpha(0).setY(y - 40 * vp.u);
    this.tweens.add({ targets: img, alpha: 1, y, scale: s, duration: d, ease: 'Back.easeOut' });
    this.time.delayedCall(d * 0.7, () => this.fx.sparkle(img.x, img.y, 10));
  }

  /** Taken down: shrinks away. */
  private decorOut(img: Phaser.GameObjects.Image): void {
    const d = dur(280);
    if (!d) {
      img.destroy();
      return;
    }
    this.tweens.add({ targets: img, alpha: 0, scale: img.scale * 0.3, duration: d, ease: 'Back.easeIn', onComplete: () => img.destroy() });
  }

  /** A spot's rectangle in canvas pixels. */
  private spotRect(p: Placement): { x: number; y: number; w: number; h: number } {
    const b = this.base;
    return { x: b.x - b.displayWidth / 2 + p.x * b.displayWidth, y: b.y - b.displayHeight / 2 + p.y * b.displayHeight, w: p.w * b.displayWidth, h: p.h * b.displayHeight };
  }

  /** Decorate mode: every spot glows (a "+" on empty ones) and takes a tap. */
  private drawMarks(): void {
    this.clearMarks();
    const u = vp.u;
    for (const [spot, p] of Object.entries(this.decorSpots)) {
      const r = this.spotRect(p);
      const filled = !!this.decorShown[spot];
      const g = this.add.graphics().setDepth(60);
      g.fillStyle(0xffffff, filled ? 0.1 : 0.3);
      g.fillRoundedRect(r.x, r.y, r.w, r.h, 14 * u);
      g.lineStyle(4 * u, 0x2ec4b6, 1);
      g.strokeRoundedRect(r.x, r.y, r.w, r.h, 14 * u);
      if (!filled) {
        const cx = r.x + r.w / 2;
        const cy = r.y + r.h / 2;
        g.fillStyle(0x2ec4b6, 1);
        g.fillCircle(cx, cy, 15 * u);
        g.fillStyle(0xffffff, 1);
        g.fillRect(cx - 8 * u, cy - 2.5 * u, 16 * u, 5 * u);
        g.fillRect(cx - 2.5 * u, cy - 8 * u, 5 * u, 16 * u);
      }
      const d = deco(800);
      if (d) this.tweens.add({ targets: g, alpha: 0.45, duration: d, yoyo: true, repeat: -1 });
      // at least a 44 CSS px target: the wall spot is about 34 px wide on a phone
      const tap = 44 * vp.dpr;
      const zone = this.add.zone(r.x + r.w / 2, r.y + r.h / 2, Math.max(r.w, tap), Math.max(r.h, tap)).setDepth(61).setInteractive({ useHandCursor: true });
      zone.on('pointerup', () => void this.pickDecor(spot));
      this.decorMarks.push(g, zone);
    }
  }

  private clearMarks(): void {
    for (const m of this.decorMarks) m.destroy();
    this.decorMarks = [];
  }

  private async toggleDecorate(): Promise<void> {
    if (this.building || this.decorBusy) return;
    const a = app();
    a.services.audio.play('button');
    if (this.decorating) {
      this.decorating = false;
      this.clearMarks();
      this.ghost?.setVisible(true);
      this.setHud();
      return;
    }
    this.decorBusy = true;
    a.ui.setBusy(true);
    await a.art.ensureGroup('decor');
    a.ui.setBusy(false);
    this.decorBusy = false;
    if (!this.scene.isActive()) return;
    this.decorating = true;
    this.ghost?.setVisible(false);
    this.drawMarks();
    this.setHud();
  }

  /** A spot tapped in Decorate mode: the sheet of owned pieces that fit (or the store for more). */
  private async pickDecor(spot: string): Promise<void> {
    if (this.decorBusy || !this.decorating) return;
    const a = app();
    this.decorBusy = true;
    a.services.audio.play('button');
    try {
      for (;;) {
        const r = await openView<DecorSheetResult>(a, 'decorSheet', { shopId: this.shop.id, spot });
        if (!r || !this.scene.isActive()) return;
        if ('store' in r) {
          await openView(a, 'store', { focus: 'decor' });
          if (!this.scene.isActive()) return;
          continue; // back to the sheet, with whatever was just bought
        }
        this.placeDecor(spot, r.decor);
        return;
      }
    } finally {
      this.decorBusy = false;
    }
  }

  /** Predict the placement (the store subscription animates it) and queue the `decor` op. */
  private placeDecor(spot: string, decorId: string | null): void {
    const s = app().services;
    let res: { ok: true } | { ok: false; reason: DecorRefusal } = { ok: false, reason: 'unknown_spot' };
    s.store.update((st) => (res = applyDecor(st, this.shop.id, spot, decorId)));
    if (!res.ok) {
      s.audio.play('nope');
      return;
    }
    s.sync.enqueue(op.decor(this.shop.id, spot, decorId));
    s.audio.play(decorId ? 'upgrade' : 'button');
  }

  private placePiece(slot: string, _kind: string, style: string, animate: boolean): Phaser.GameObjects.Image {
    const img = this.pieceImage(slot, style);
    this.pieces.get(slot)?.destroy();
    this.pieces.set(slot, img);
    if (animate) this.buildAnim(img);
    return img;
  }

  private showGhost(slot: string, _kind: string, style: Style): void {
    this.ghost?.destroy();
    const img = this.pieceImage(slot, style).setAlpha(0.5);
    const d = deco(700);
    if (d) this.tweens.add({ targets: img, alpha: 0.25, duration: d, yoyo: true, repeat: -1 });
    this.ghost = img;
  }

  /** MT-02.1: 1-second build — dust, drop-in with bounce, then confetti. */
  private buildAnim(img: Phaser.GameObjects.Image): void {
    const d = dur(1000);
    const u = vp.u;
    if (!d) return;
    const s = img.scale;
    const y = img.y;
    img.setScale(s * 0.2).setAlpha(0).setY(y - 60 * u);
    this.fx.burst(img.x, y, 0xe8d6bd, 20);
    this.tweens.add({ targets: img, alpha: 1, y, scale: s * 1.15, duration: d * 0.45, ease: 'Back.easeOut', delay: d * 0.1 });
    this.tweens.add({ targets: img, scale: s, duration: d * 0.3, delay: d * 0.55, ease: 'Sine.easeInOut' });
    this.time.delayedCall(d * 0.85, () => {
      this.fx.confetti(img.x, img.y, 60);
      this.fx.sparkle(img.x, img.y, 10);
    });
  }

  private setHud(): void {
    // async flows (build animation, popups) may finish after the player left this scene
    if (!this.scene.isActive()) return;
    const a = app();
    a.ui.setHud({
      mode: 'shop',
      map: null,
      play: null,
      shop: {
        shopId: this.shop.id,
        selected: { ...this.selected },
        building: this.building,
        onSelect: (slot, style) => {
          this.selected[slot] = style;
          const def = this.shop.upgrades.find((x) => x.slot === slot);
          if (def) this.showGhost(slot, def.id, style);
          a.services.audio.play('button');
          this.setHud();
        },
        onBuy: (slot, style) => void this.buy(slot, style),
        onRestock: (slot, rareItem) => void this.restock(slot, rareItem),
        onBack: () => (this.visit ? this.visit.back() : go(this, 'map')),
        visit: this.visit ? { owner: this.visit.owner, trophy: this.visit.trophy } : null,
        decorate:
          this.visit || !Object.keys(this.decorSpots).length
            ? null
            : { active: this.decorating, onToggle: () => void this.toggleDecorate(), onStore: () => void openView(app(), 'store', { focus: 'decor' }) },
        onPlay: () => {
          const { first, last } = a.services.levels.range(a.city);
          go(this, 'play', { level: currentLevelNumber(a.services.store.state, last, a.city, first) });
        },
      },
    });
  }

  private async buy(slot: string, style: Style): Promise<void> {
    if (this.building) return;
    const a = app();
    const s = a.services;
    let res = { ok: false, reason: 'locked' } as ReturnType<typeof applyUpgrade>;
    s.store.update((st) => (res = applyUpgrade(st, this.shop.id, slot, style)));
    if (!res.ok) {
      s.audio.play('nope');
      return;
    }
    const def = this.shop.upgrades.find((x) => x.slot === slot)!;
    s.sync.enqueue(op.upgrade(this.shop.id, slot, style));
    s.store.update(noteUpgrade); // the rating ask's "first shop upgrade" (services/review.ts)
    const count = upgradeCount(s.store.state, this.shop.id);
    s.analytics.track('shop_upgrade', { shop: this.shop.id, slot, style, coins: res.coins, count });
    if (count === 1) s.analytics.track('tutorial_step', { step: 'first_upgrade' });
    if (count === 2) s.analytics.track('tutorial_step', { step: 'second_upgrade' });
    this.building = true;
    this.setHud();
    this.ghost?.destroy();
    this.ghost = null;
    s.audio.play('upgrade');
    this.placePiece(slot, def.id, style, true);
    a.ui.setHud({ coinPulse: a.ui.hud.coinPulse + 1 });
    await wait(1100);
    this.building = false;
    const next = nextUpgrade(s.store.state, this.shop.id);
    if (next) this.showGhost(next.slot, next.id, this.selected[next.slot] ?? 'a');
    this.setHud();
    await this.afterUpgrade(count);
  }

  private async afterUpgrade(count: number): Promise<void> {
    const a = app();
    const s = a.services;
    const st = s.store.state;
    const firstShop = cityShops(a.city)[0]!.id === this.shop.id;
    if (firstShop && count === 1) a.ui.toast(t('shop.firstDone'));
    if (firstShop && count === 2 && !st.shopName) {
      const name = await a.ui.open('nameShop', { current: st.shopName });
      s.store.update((x) => {
        mark(x, MARK.shopNamed);
        if (name) x.shopName = name;
      });
      if (name) s.sync.enqueue(op.shopName(name));
      s.analytics.track('tutorial_step', { step: name ? 'shop_named' : 'shop_name_skipped' });
      this.setHud();
      await openView(a, 'savePrompt', { again: false });
      s.store.update((x) => {
        mark(x, MARK.savePrompt);
        x.flags.savePrompts++;
      });
      s.analytics.track('tutorial_step', { step: 'save_prompt' });
    }
    if (isGrandOpeningDue(s.store.state, this.shop.id)) await this.grandOpening();
  }

  private async grandOpening(): Promise<void> {
    const a = app();
    const s = a.services;
    const list = cityShops(a.city);
    const next = list[list.findIndex((x) => x.id === this.shop.id) + 1] ?? null;
    this.fx.confetti(this.scale.width / 2, this.scale.height * 0.3, 120);
    s.audio.play('win');
    const name = shopTitle(s.store.state, this.shop.id, this.shop.name);
    const r = await a.ui.open('grandOpening', { shopName: name, nextShop: next ? shopTitle(s.store.state, next.id, next.name) : null });
    s.store.update((st) => st.flags.grandOpenings.push(this.shop.id));
    s.analytics.track('grand_opening', { shop: this.shop.id });
    if (r === 'share') await openView(a, 'shareComposer', { kind: 'shop', shopName: name, shopId: this.shop.id });
    // MO-03.2: the Starter Pack offer opens once the first shop is complete
    await openView(a, 'starterOffer', { source: 'grandOpening' }).catch(() => undefined);
  }

  /** SO-01: the slot needs a rare item — ask a friend, buy it with gems, or watch a fast restock. */
  private async restock(slot: string, rareItem: string): Promise<void> {
    const a = app();
    const got = await openView<boolean>(a, 'restock', { shopId: this.shop.id, slot, rareItem });
    this.setHud();
    if (got) a.services.audio.play('coin');
  }

  /** CSS-px positions for tests: the base, and each decor spot's centre (`spot_<id>`). */
  targets(): Record<string, { x: number; y: number }> {
    if (!this.base) return {};
    const out: Record<string, { x: number; y: number }> = { base: { x: this.base.x / vp.dpr, y: this.base.y / vp.dpr } };
    for (const [spot, p] of Object.entries(this.decorSpots)) {
      const r = this.spotRect(p);
      out[`spot_${spot}`] = { x: (r.x + r.w / 2) / vp.dpr, y: (r.y + r.h / 2) / vp.dpr };
    }
    return out;
  }
}
